/**
 * UPT-2124 J4a (spec `web/SPEC-UPT-2124-J4.md` §2 n° 3, §3) — le FANTÔME : un liseré accent autour de l'élément visé et une étiquette
 * (« Géode : cliquer sur Enregistrer ») pendant 900 ms, avec un bouton « Annuler » ; la touche `Échap` annule aussi (voir `uiGhostStore`).
 * Rendu en `fixed` au-dessus du contenu, sans toucher au DOM de la page (pas de portail dans le périmètre de l'acteur : `data-voice-exclude`).
 * `prefers-reduced-motion` : le liseré reste fixe (la pulsation n'est jouée que sous `motion-safe`), même délai.
 * Étiquette annoncée une fois (`role="status"`).
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from '@/react-intl';
import { cancelUiGhost, subscribeUiGhost, uiGhostState } from './uiGhostStore';

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

const PADDING = 4;

const measure = (element: HTMLElement): Box => {
  const rect = element.getBoundingClientRect();
  return { left: rect.left - PADDING, top: rect.top - PADDING, width: rect.width + PADDING * 2, height: rect.height + PADDING * 2 };
};

const sameBox = (a: Box | null, b: Box): boolean => a !== null && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;

export function UiGhost() {
  const intl = useIntl();
  const ghost = useSyncExternalStore(subscribeUiGhost, uiGhostState, () => null);
  const [box, setBox] = useState<Box | null>(null);
  const element = ghost?.element ?? null;

  // Suit l'élément pendant le fantôme (défilement, redimensionnement, mise en page qui bouge).
  useEffect(() => {
    if (!element) {
      setBox(null);
      return undefined;
    }
    let frame = 0;
    const tick = () => {
      const next = measure(element);
      setBox((previous) => (sameBox(previous, next) ? previous : next));
      frame = window.requestAnimationFrame(tick);
    };
    tick();
    return () => window.cancelAnimationFrame(frame);
  }, [element, ghost?.id]);

  if (!ghost || typeof document === 'undefined') {
    return null;
  }

  const labelAbove = box ? box.top > 48 : true;
  const chip = (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto flex items-center gap-2 rounded-md bg-portal-accent px-3 py-1 text-sm font-semibold text-portal-ink-on-accent shadow-lg"
    >
      <span>{ghost.label}</span>
      {ghost.cancellable ? (
        <button
          type="button"
          onClick={() => cancelUiGhost()}
          data-voice-ghost-cancel
          className="inline-flex min-h-11 items-center rounded border border-portal-ink-on-accent px-3 text-sm font-semibold"
        >
          {intl.formatMessage({ id: 'voice.ui.ghost.cancel' })}
        </button>
      ) : null}
    </div>
  );

  return createPortal(
    <div data-voice-exclude="true" data-voice-ghost={ghost.mode} className="pointer-events-none fixed inset-0 z-[90]">
      {element && box ? (
        <>
          <div
            aria-hidden="true"
            style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
            className="absolute rounded-md border-2 border-portal-accent motion-safe:animate-pulse"
          />
          <div style={{ left: Math.max(4, box.left), top: labelAbove ? Math.max(4, box.top - 52) : box.top + box.height + 6 }} className="absolute max-w-[90vw]">
            {chip}
          </div>
        </>
      ) : null}
      {!element ? <div className="absolute inset-x-0 top-4 flex justify-center px-4">{chip}</div> : null}
    </div>,
    document.body
  );
}

export default UiGhost;
